/**
 * Safe inline HTML for sortable_list item text.
 * Supports author `<a href>` links and interlink module output
 * (`<a class="interlink">`, `<span class="interlink-missing">`).
 */

const ANCHOR_RE = /<a\b([^>]*)>([\s\S]*?)<\/a>/i;
const MISSING_SPAN_RE =
	/<span\b([^>]*\bclass\s*=\s*["'][^"']*\binterlink-missing\b[^"']*["'][^>]*)>([\s\S]*?)<\/span>/i;

const SAFE_HREF_RE = /^(https?:\/\/|\/|#|mailto:|tel:)[^\s<>"']*$/i;

function escapeHtml(s: string): string {
	return s
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function escapeAttr(s: string): string {
	return escapeHtml(s).replace(/'/g, "&#39;");
}

function decodeBasicEntities(s: string): string {
	return s
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&amp;/g, "&");
}

function attrValue(attrs: string, name: string): string | null {
	const re = new RegExp(
		`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`,
		"i"
	);
	const m = re.exec(attrs);
	if (!m) {
		return null;
	}
	return (m[1] ?? m[2] ?? m[3] ?? "").trim();
}

function isSafeHref(href: string): boolean {
	const t = href.trim();
	if (!t || t.toLowerCase().startsWith("javascript:")) {
		return false;
	}
	return SAFE_HREF_RE.test(t);
}

function stripTags(s: string): string {
	return s.replace(/<[^>]+>/g, "");
}

function findNextMarkup(
	text: string,
	from: number
): {
	start: number;
	end: number;
	kind: "anchor" | "missing";
	match: RegExpExecArray;
} | null {
	const slice = text.slice(from);
	const anchor = ANCHOR_RE.exec(slice);
	const missing = MISSING_SPAN_RE.exec(slice);
	const anchorStart = anchor
		? from + (anchor.index ?? 0)
		: Number.POSITIVE_INFINITY;
	const missingStart = missing
		? from + (missing.index ?? 0)
		: Number.POSITIVE_INFINITY;
	if (!(anchor || missing)) {
		return null;
	}
	if (anchor && anchorStart <= missingStart) {
		return {
			start: anchorStart,
			end: anchorStart + anchor[0].length,
			kind: "anchor",
			match: anchor,
		};
	}
	if (!missing) {
		return null;
	}
	return {
		start: missingStart,
		end: missingStart + missing[0].length,
		kind: "missing",
		match: missing,
	};
}

/** True when item text should render as HTML (anchors / interlink markup). */
export function sortableListItemNeedsHtml(text: string): boolean {
	const t = text.trim();
	if (!t) {
		return false;
	}
	return (
		/<a\b/i.test(t) ||
		/class\s*=\s*["'][^"']*\binterlink(-missing)?\b/i.test(t) ||
		t.includes("[[")
	);
}

/**
 * Sanitize item text for `dangerouslySetInnerHTML`.
 * Plain text is escaped; safe anchors and interlink chips are preserved.
 * Unresolved `[[...]]` shortcodes are left as escaped text (Region/preview
 * rewrite them before render when interlinking is enabled).
 */
export function sanitizeSortableListItemHtml(source: string): string {
	const text = source ?? "";
	if (!text) {
		return "";
	}
	if (!sortableListItemNeedsHtml(text)) {
		return escapeHtml(text);
	}

	const parts: string[] = [];
	let cursor = 0;
	let next = findNextMarkup(text, cursor);
	while (next) {
		if (next.start > cursor) {
			parts.push(escapeHtml(text.slice(cursor, next.start)));
		}
		const attrs = next.match[1] ?? "";
		const inner = next.match[2] ?? "";
		const label = escapeHtml(stripTags(decodeBasicEntities(inner)));
		if (next.kind === "anchor") {
			const href = attrValue(attrs, "href");
			const className = attrValue(attrs, "class") ?? "";
			const entryId = attrValue(attrs, "data-interlink-entry-id");
			const preview = attrValue(attrs, "data-interlink-preview");
			const rel = attrValue(attrs, "rel") ?? "";
			const target = attrValue(attrs, "target") ?? "";
			if (href && isSafeHref(href)) {
				const classAttr = className ? ` class="${escapeAttr(className)}"` : "";
				const entryAttr =
					entryId && entryId.length > 0
						? ` data-interlink-entry-id="${escapeAttr(entryId)}"`
						: "";
				const previewAttr =
					preview === "0" || preview?.toLowerCase() === "false"
						? ` data-interlink-preview="${escapeAttr(preview)}"`
						: "";
				const relAttr = rel ? ` rel="${escapeAttr(rel)}"` : "";
				const targetAttr =
					target === "_blank" || target === "_self"
						? ` target="${escapeAttr(target)}"`
						: "";
				parts.push(
					`<a${classAttr}${entryAttr}${previewAttr}${relAttr}${targetAttr} href="${escapeAttr(href)}">${label}</a>`
				);
			} else {
				parts.push(label);
			}
		} else {
			const missing = attrValue(attrs, "data-interlink-missing") ?? "";
			parts.push(
				`<span class="interlink-missing"${
					missing ? ` data-interlink-missing="${escapeAttr(missing)}"` : ""
				}>${label}</span>`
			);
		}
		cursor = next.end;
		next = findNextMarkup(text, cursor);
	}
	if (cursor < text.length) {
		parts.push(escapeHtml(text.slice(cursor)));
	}
	return parts.join("");
}
